/**
 * The inbox's right pane: who sent the open submission, the four acts on it, the client's chart
 * strip and the submission itself.
 *
 * ── The same report everywhere ───────────────────────────────────────────────────────────────
 * Owner rule, 17 September 2026: a submission is read in THE SAME view wherever it is opened. So
 * the answers are `features/survey-report`'s `SurveyReport`, built from the client's own history
 * read — the one the client's Ankiety tab makes, and usually the same cache entry — with its
 * readings, deltas, units and media. The strip over it is that tab's strip, over the same eight
 * weeks (`surveyChartWindow`).
 *
 * ── Not keyed by the submission ──────────────────────────────────────────────────────────────
 * Stepping with J or K changes the props, not the element: a second submission of a client already
 * read lands at once from the cache, and a new client's read is a gated wait inside the boxes the
 * pane already holds. Remounting would replay the entrance on every step.
 *
 * ── „Co się zmieniło" is not drawn ───────────────────────────────────────────────────────────
 * The canvas drew a sentence over the charts. `model/biggestChange` names which answer moved most
 * but writes no sentence, and this pane does not invent one.
 */

import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import {
  NextIcon,
  PreviousIcon,
  reportOf,
  SurveyChartsStrip,
  surveyChartWindow,
  SurveyReport,
  useSurveyHistoryViews,
} from '~/features/survey-report';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { CheckIcon } from '~/shared/ui/AuthIcons';
import { EmptyState } from '~/shared/ui/EmptyState';
import { InboxMarkIcon, NoteMarkIcon } from '~/shared/ui/EmptyStateIcons';
import ember from '~/shared/ui/emberButton.module.css';
import ghost from '~/shared/ui/ghostButton.module.css';
import kbd from '~/shared/ui/kbd.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import stateAction from '~/shared/ui/stateAction.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { ReviewRow } from '../model/reviewQueue';
import styles from './ReviewQueue.module.css';

const REPORT = 'web:surveyReport';

interface ReviewPaneProps {
  readonly row: ReviewRow;
  readonly previousId: number | null;
  readonly nextId: number | null;
  readonly onOpen: (assignmentId: number) => void;
  readonly onReviewNext: () => void;
  readonly marking: boolean;
  readonly reducedMotion: boolean;
}

export function ReviewPane({
  row,
  previousId,
  nextId,
  onOpen,
  onReviewNext,
  marking,
  reducedMotion,
}: ReviewPaneProps) {
  const { t } = useTranslation();
  const { view, strip, pending, failed, retry } = useSurveyHistoryViews(row.customerId, {
    chartRange: surveyChartWindow(new Date()),
    occurrenceId: row.assignmentId,
    lastCadencedId: null,
  });
  const gate = useSkeletonGate(pending);
  const subject = reportOf(view, row.assignmentId);

  return (
    <section className={styles.pane} aria-labelledby="review-pane-title">
      <div className={styles.paneHead}>
        <div className={styles.paneHeadText}>
          <h2 id="review-pane-title" className={styles.paneTitle}>
            {row.name}
            <span className={styles.paneForm}> · {row.templateTitle}</span>
          </h2>
          <p className={styles.paneMeta}>{row.meta}</p>
        </div>

        <div className={styles.paneActions}>
          <StepButton
            target={previousId}
            label={t('web:reports.queue.previous')}
            onOpen={onOpen}
            glyph={<PreviousIcon />}
          />
          <StepButton
            target={nextId}
            label={t('web:reports.queue.next')}
            onOpen={onOpen}
            glyph={<NextIcon />}
          />
          <Link
            to="/czat"
            search={{ klient: row.customerId }}
            className={`${String(ghost.ghost)} ${String(styles.paneGhost)}`}
            aria-label={t('web:reports.queue.writeLabel', { client: row.name })}
          >
            {t('web:reports.queue.write')}
          </Link>
          <button
            type="button"
            className={`${String(ember.ember)} ${String(ember.pill)} ${String(styles.panePrimary)}`}
            // `aria-disabled`, never `disabled`: the control keeps its place in the tab order while
            // the write is in flight.
            aria-disabled={marking ? true : undefined}
            aria-keyshortcuts="E"
            onClick={onReviewNext}
          >
            <CheckIcon className={styles.panePrimaryIcon} />
            {t('web:reports.queue.reviewNext')}
            <kbd className={`${String(kbd.kbd)} ${String(styles.paneKbd)}`} aria-hidden>
              E
            </kbd>
          </button>
        </div>
      </div>

      <div className={styles.paneBody}>
        <SurveyChartsStrip
          series={strip.series}
          xDomain={strip.xDomain}
          gate={gate}
          failed={failed}
          silent={failed}
          reducedMotion={reducedMotion}
        />

        {gate.waiting ? (
          <div className={styles.paneReserve} aria-busy="true">
            <span className={hidden.srOnly}>{t(`${REPORT}.loading`)}</span>
            {gate.skeleton
              ? [0, 1, 2, 3].map((block) => (
                  <i key={block} className={`${String(skel.bar)} ${String(styles.skelBlock)}`} />
                ))
              : null}
          </div>
        ) : failed ? (
          <div className={`${String(styles.paneState)} ${gate.reveal}`}>
            <EmptyState
              mark={<NoteMarkIcon />}
              tone="neutral"
              title={t(`${REPORT}.failedTitle`)}
              hint={t(`${REPORT}.failedHint`)}
            />
            <button
              type="button"
              className={`${String(ghost.ghost)} ${String(stateAction.stateAction)}`}
              onClick={retry}
            >
              {t(`${REPORT}.retry`)}
            </button>
          </div>
        ) : subject.status === 'missing' ? (
          <div className={`${String(styles.paneState)} ${gate.reveal}`}>
            <EmptyState
              mark={<InboxMarkIcon />}
              tone="neutral"
              title={t(`${REPORT}.missingTitle`)}
              hint={t(`${REPORT}.missingHint`)}
            />
          </div>
        ) : (
          <section
            className={`${String(styles.paneReport)} ${gate.reveal}`}
            aria-label={t('web:reports.queue.reportLabel')}
          >
            <SurveyReport view={view} />
          </section>
        )}
      </div>
    </section>
  );
}

/** ‹ and ›: a step through the queue, refused in words at either end. */
function StepButton({
  target,
  label,
  onOpen,
  glyph,
}: {
  readonly target: number | null;
  readonly label: string;
  readonly onOpen: (assignmentId: number) => void;
  readonly glyph: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={`${String(ghost.ghost)} ${String(styles.paneStep)}`}
      aria-label={label}
      title={label}
      aria-disabled={target === null ? true : undefined}
      onClick={() => {
        if (target !== null) onOpen(target);
      }}
    >
      {glyph}
    </button>
  );
}
